import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../contexts/SettingsContext';
import { playSound } from '../../utils/sounds';
import RankBadge from '../elo/RankBadge';
import './Queue.css';

/**
 * Interactive demo page — simulates the full queue flow locally.
 * No backend calls. Use /queue-demo (gated by SHOW_QUEUE_DEMO feature flag)
 * to test join → fill → accept modal → match-found sound → match creation
 * without affecting real production queue state.
 */

const DEMO_ME = {
  discord_id: 'demo-me',
  username: 'Du (demo)',
  elo: { elo: 1150, rank: 'Platinum' },
  avatar: null,
};

const FAKE_PLAYERS = Array.from({ length: 9 }, (_, i) => ({
  discord_id: `fake-${i + 1}`,
  username: `Demo Player ${i + 1}`,
  elo: { elo: 900 + i * 50, rank: ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'][Math.min(4, Math.floor(i / 2))] },
  avatar: null,
}));

const ACCEPT_TIMEOUT = 30;
const OTHER_ACCEPT_INTERVAL = 1500;

/**
 * Pure-frontend simulation of the queue flow — empty queue → join →
 * fill to 10 → 30s accept modal with staggered fake-player accepts →
 * "Match skapad" screen. Plays the configured match-found sound at the
 * right moment so audio settings can be validated without spinning up
 * real players. Gated by SHOW_QUEUE_DEMO feature flag.
 */
export default function QueueDemo() {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const [queue, setQueue] = useState([]);
  const [locked, setLocked] = useState(false);
  const [acceptModal, setAcceptModal] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [matchCreated, setMatchCreated] = useState(false);
  const countdownRef = useRef(null);
  const acceptTimersRef = useRef([]);

  const cleanupTimers = useCallback(() => {
    acceptTimersRef.current.forEach(clearTimeout);
    acceptTimersRef.current = [];
  }, []);

  useEffect(() => () => cleanupTimers(), [cleanupTimers]);

  const reset = useCallback(() => {
    cleanupTimers();
    setQueue([]);
    setLocked(false);
    setAcceptModal(null);
    setCountdown(null);
    setMatchCreated(false);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, [cleanupTimers]);

  const triggerMatchFound = useCallback((players) => {
    const statuses = {};
    players.forEach(p => { statuses[p.discord_id] = 'pending'; });
    setLocked(true);
    setAcceptModal({ players, statuses });
    setCountdown(ACCEPT_TIMEOUT);
    playSound(settingsRef.current.matchFoundSound, settingsRef.current.soundVolume);
  }, []);

  const addPlayer = (player) => {
    setQueue(prev => {
      if (prev.some(p => p.discord_id === player.discord_id)) return prev;
      if (prev.length >= 10) return prev;
      const next = [...prev, player];
      if (next.length === 10) {
        setTimeout(() => triggerMatchFound(next), 500);
      }
      return next;
    });
  };

  const removePlayer = (discordId) => {
    setQueue(prev => prev.filter(p => p.discord_id !== discordId));
  };

  const fillQueue = () => {
    const inQueue = new Set(queue.map(p => p.discord_id));
    const toAdd = FAKE_PLAYERS.filter(p => !inQueue.has(p.discord_id)).slice(0, 10 - queue.length);
    if (toAdd.length === 0) return;
    setQueue(prev => {
      const next = [...prev, ...toAdd].slice(0, 10);
      if (next.length === 10) {
        setTimeout(() => triggerMatchFound(next), 500);
      }
      return next;
    });
  };

  const addOneFake = () => {
    const inQueue = new Set(queue.map(p => p.discord_id));
    const next = FAKE_PLAYERS.find(p => !inQueue.has(p.discord_id));
    if (next) addPlayer(next);
  };

  const joinSelf = () => addPlayer(DEMO_ME);
  const leaveSelf = () => removePlayer(DEMO_ME.discord_id);

  const handleAccept = () => {
    setAcceptModal(prev => {
      if (!prev) return null;
      return { ...prev, statuses: { ...prev.statuses, [DEMO_ME.discord_id]: 'accepted' } };
    });
    scheduleOtherAccepts();
  };

  const handleDecline = () => {
    cleanupTimers();
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setAcceptModal(null);
    setCountdown(null);
    setLocked(false);
    setQueue(prev => prev.filter(p => p.discord_id !== DEMO_ME.discord_id));
  };

  const scheduleOtherAccepts = () => {
    if (!acceptModal) return;
    cleanupTimers();
    const others = acceptModal.players.filter(p => p.discord_id !== DEMO_ME.discord_id);
    others.forEach((player, i) => {
      const t = setTimeout(() => {
        setAcceptModal(prev => {
          if (!prev) return null;
          return { ...prev, statuses: { ...prev.statuses, [player.discord_id]: 'accepted' } };
        });
      }, (i + 1) * OTHER_ACCEPT_INTERVAL);
      acceptTimersRef.current.push(t);
    });
  };

  // Detect all-accepted → match created
  useEffect(() => {
    if (!acceptModal) return;
    const statuses = acceptModal.statuses || {};
    const allAccepted = acceptModal.players.every(p => statuses[p.discord_id] === 'accepted');
    if (allAccepted) {
      cleanupTimers();
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      setAcceptModal(null);
      setCountdown(null);
      setMatchCreated(true);
    }
  }, [acceptModal, cleanupTimers]);

  // Countdown
  useEffect(() => {
    if (countdown === null || countdown <= 0) {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          // Time's up → auto-decline (demo: treat as cancel)
          cleanupTimers();
          setAcceptModal(null);
          setLocked(false);
          clearInterval(countdownRef.current);
          countdownRef.current = null;
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [countdown !== null && countdown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const isInQueue = queue.some(p => p.discord_id === DEMO_ME.discord_id);
  const userAccepted = acceptModal?.statuses?.[DEMO_ME.discord_id] === 'accepted';

  if (matchCreated) {
    return (
      <div className="queue-page">
        <h1 className="queue-title">{t('queue.demoTitle')}</h1>
        <div className="queue-card" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎮</div>
          <h2 style={{ marginTop: 0 }}>{t('queue.demoMatchCreated')}</h2>
          <p style={{ color: '#888', marginBottom: '2rem' }}>
            {t('queue.demoProductionNote')}
          </p>
          <button className="queue-btn join" onClick={reset}>{t('queue.demoRestart')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="queue-page">
      <h1 className="queue-title">{t('queue.demoTitle')}</h1>
      <p style={{ textAlign: 'center', color: '#888', marginTop: '-0.5rem', marginBottom: '1rem', fontSize: '0.85rem' }}>
        {t('queue.demoSubtitle')}
      </p>

      <div className="queue-card">
        <div className="queue-header">
          <span className="queue-count">{queue.length} / 10</span>
          <span className="queue-status">
            {locked ? t('queue.matchFound') : t('queue.waitingPlayers')}
          </span>
        </div>

        <div className="queue-players">
          {queue.map((player, i) => (
            <div key={player.discord_id} className="queue-player">
              <span className="queue-player-num">{i + 1}</span>
              <span className="queue-player-status online" title={t('queue.online')} />
              <span className="queue-player-name">{player.username}</span>
              {player.elo && (
                <span className="queue-player-elo">
                  <RankBadge rank={player.elo.rank} size="small" />
                  {player.elo.elo}
                </span>
              )}
              {player.discord_id !== DEMO_ME.discord_id && !locked && (
                <button
                  className="queue-kick-btn"
                  title={t('queue.removeTitle', { name: player.username })}
                  onClick={() => removePlayer(player.discord_id)}
                >
                  X
                </button>
              )}
            </div>
          ))}

          {Array.from({ length: 10 - queue.length }).map((_, i) => (
            <div key={`empty-${i}`} className="queue-player empty">
              <span className="queue-player-num">{queue.length + i + 1}</span>
              <span className="queue-player-name empty-slot">{t('queue.waitingSlot')}</span>
            </div>
          ))}
        </div>

        <div className="queue-actions" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
          {isInQueue ? (
            <button className="queue-btn leave" onClick={leaveSelf} disabled={locked}>
              {t('queue.leave')}
            </button>
          ) : (
            <button className="queue-btn join" onClick={joinSelf} disabled={locked || queue.length >= 10}>
              {t('queue.join')}
            </button>
          )}
          <button
            onClick={addOneFake}
            disabled={locked || queue.length >= 10}
            style={{
              padding: '0.7rem 1.2rem',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent',
              color: '#ddd',
              cursor: locked || queue.length >= 10 ? 'not-allowed' : 'pointer',
              opacity: locked || queue.length >= 10 ? 0.5 : 1,
            }}
          >
            {t('queue.fakePlayer')}
          </button>
          <button
            onClick={fillQueue}
            disabled={locked || queue.length >= 10}
            style={{
              padding: '0.7rem 1.2rem',
              borderRadius: 8,
              border: '1px solid rgba(74,222,128,0.4)',
              background: 'rgba(74,222,128,0.1)',
              color: '#4ade80',
              cursor: locked || queue.length >= 10 ? 'not-allowed' : 'pointer',
              opacity: locked || queue.length >= 10 ? 0.5 : 1,
            }}
          >
            {t('queue.fillQueue')}
          </button>
          <button
            onClick={reset}
            style={{
              padding: '0.7rem 1.2rem',
              borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.15)',
              background: 'transparent',
              color: '#888',
              cursor: 'pointer',
            }}
          >
            {t('queue.clear')}
          </button>
        </div>
      </div>

      {acceptModal && (
        <div className="accept-modal-overlay">
          <div className="accept-modal">
            <h2>{t('queue.matchFound')}</h2>
            {countdown !== null && countdown > 0 && (
              <div className="accept-countdown">{countdown}s</div>
            )}
            <div className="accept-player-list">
              {acceptModal.players.map(p => {
                const status = acceptModal.statuses?.[p.discord_id] || 'pending';
                const icon = status === 'accepted' ? '✅' : status === 'declined' ? '❌' : '⏳';
                return (
                  <div key={p.discord_id} className={`accept-player ${status}`}>
                    <span className="accept-player-icon">{icon}</span>
                    <span className="accept-player-name">{p.username}</span>
                  </div>
                );
              })}
            </div>
            {userAccepted ? (
              <p className="accept-waiting">{t('queue.waitingOthers')}</p>
            ) : (
              <div className="accept-buttons">
                <button className="accept-btn" onClick={handleAccept}>{t('queue.accept')}</button>
                <button className="decline-btn" onClick={handleDecline}>{t('queue.decline')}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
